import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const WhatsAppButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const { language } = useLanguage();

  const messages = {
    it: {
      greeting: "Ciao! Come possiamo aiutarti?",
      book: "Vorrei prenotare un tavolo",
      info: "Vorrei informazioni",
      menu: "Vorrei vedere il menu",
      placeholder: "Scrivi un messaggio..."
    },
    de: {
      greeting: "Hallo! Wie können wir Ihnen helfen?",
      book: "Ich möchte einen Tisch reservieren",
      info: "Ich möchte Informationen",
      menu: "Ich möchte die Speisekarte sehen",
      placeholder: "Nachricht schreiben..."
    },
    en: {
      greeting: "Hello! How can we help you?",
      book: "I would like to book a table",
      info: "I would like information",
      menu: "I would like to see the menu",
      placeholder: "Write a message..."
    }
  };

  const t = messages[language];

  // Format WhatsApp number (remove spaces and add country code if needed)
  const whatsappNumber = restaurantInfo.whatsapp.replace(/\s/g, '');
  const formattedNumber = whatsappNumber.startsWith('0') 
    ? '41' + whatsappNumber.substring(1) 
    : whatsappNumber;

  const openWhatsApp = (message = '') => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${formattedNumber}?text=${encodedMessage}`, '_blank');
    setIsOpen(false);
  };

  useEffect(() => {
    // Stop pulse after 10 seconds
    const timer = setTimeout(() => setShowPulse(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        {/* Popup */}
        {isOpen && (
          <div className="absolute bottom-16 right-0 w-72 bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn mb-2">
            {/* Header */}
            <div className="bg-[#25D366] px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#25D366]" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Mezzaluna</p>
                  <p className="text-white/80 text-xs">Online</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Chat area */}
            <div className="p-4 bg-[#ECE5DD]">
              <div className="bg-white rounded-lg p-3 shadow-sm mb-3">
                <p className="text-gray-800 text-sm">{t.greeting}</p>
              </div>
              
              {/* Quick replies */}
              <div className="space-y-2">
                <button 
                  onClick={() => openWhatsApp(t.book)}
                  className="w-full text-left px-3 py-2 bg-white rounded-lg text-sm text-[#25D366] hover:bg-gray-50 transition-colors border border-[#25D366]/20"
                >
                  📅 {t.book}
                </button>
                <button 
                  onClick={() => openWhatsApp(t.info)}
                  className="w-full text-left px-3 py-2 bg-white rounded-lg text-sm text-[#25D366] hover:bg-gray-50 transition-colors border border-[#25D366]/20"
                >
                  ℹ️ {t.info}
                </button>
                <button 
                  onClick={() => openWhatsApp(t.menu)}
                  className="w-full text-left px-3 py-2 bg-white rounded-lg text-sm text-[#25D366] hover:bg-gray-50 transition-colors border border-[#25D366]/20"
                >
                  📋 {t.menu}
                </button>
              </div>
            </div>

            {/* Input */}
            <div className="p-3 bg-[#F0F0F0] flex gap-2">
              <input 
                type="text" 
                placeholder={t.placeholder}
                className="flex-1 px-3 py-2 rounded-full text-sm border-0 focus:outline-none focus:ring-2 focus:ring-[#25D366]"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value) {
                    openWhatsApp(e.target.value);
                  }
                }}
              />
              <button 
                onClick={() => openWhatsApp('')}
                className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center text-white hover:bg-[#1da851] transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Main button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`relative w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1da851] transition-all duration-300 hover:scale-110 ${isOpen ? 'rotate-0' : ''}`}
        >
          {/* Pulse effect */}
          {showPulse && !isOpen && (
            <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-75"></span>
          )}
          
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          )}
        </button>
      </div>
    </>
  );
};

export default WhatsAppButton;
